// Climate server job API response types
export interface ClimateJobResponse {
  success: boolean;
  jobs: Job[];
  pagination: {
    limit: number;
    offset: number;
    total_count: number;
    returned_count: number;
  };
  filters: {
    status: Record<string, unknown>;
    job_type: Record<string, unknown>;
    since: Record<string, unknown>;
  };
  timestamp: string;
}

export interface Job {
  job_id: string;
  job_type: string;
  status: JobStatus;
  progress: number;
  message: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  timeout_seconds: number;
  parent_request_id?: string;
}

export interface JobDetail extends Job {
  input: JobInput;
  output?: JobOutput;
  error_details?: unknown;
  control_flags: {
    cancel_requested: boolean;
    pause_requested: boolean;
    timeout_at: string;
  };
}

export interface ClimateJobDetailResponse {
  success: boolean;
  job_id: string;
  job_type: string;
  status: JobStatus;
  progress: number;
  message: string;
  created_at: string;
  updated_at: string;
  input: JobInput;
  output?: JobOutput;
  error_details?: unknown;
  control_flags: {
    cancel_requested: boolean;
    pause_requested: boolean;
    timeout_at: string;
  };
  timestamp: string;
}

export interface JobInput {
  end_date?: string;
  variable?: string;
  region_id?: string;
  start_date?: string;
  data_source?: string;
  force_download?: boolean;
  [key: string]: unknown; // Allow additional input parameters
}

export interface JobOutput {
  action: string;
  job_id: string;
  success: boolean;
  end_date?: string;
  variable?: string;
  region_id?: string;
  timestamp: string;
  start_date?: string;
  total_days?: number;
  data_source?: string;
  region_name?: string;
  region_type?: string;
  final_status?: string;
  existing_days?: number;
  missing_ranges?: number;
  download_results?: DownloadResult[];
  downloaded_records?: number;
  optimization_percent?: number;
  [key: string]: unknown; // Allow additional output parameters
}

export interface DownloadResult {
  success: boolean;
  start_date: string;
  end_date: string;
  total_dates: number;
  success_rate: number;
  records_processed: number;
  errors?: JobError[];
}

export interface JobError {
  date: string;
  error: string;
}

export type JobStatus = 
  | 'queued' 
  | 'running' 
  | 'completed' 
  | 'error' 
  | 'cancelled' 
  | 'paused' 
  | 'timeout'
  | 'pending'
  | 'failed'
  | 'partial';

export type JobType = 
  | 'data_intake'
  | 'data_processing'
  | 'data_validation'
  | 'data_export'
  | 'maintenance'
  | 'cleanup';

// Frontend request interfaces
export interface JobListRequest {
  status?: JobStatus;
  job_type?: JobType;
  since?: string;
  limit?: number;
  offset?: number;
}

export interface JobListFilters {
  status?: JobStatus;
  job_type?: JobType;
  since?: string;
  search?: string;
}

export interface JobPaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// UI display interfaces
export interface JobDisplayInfo {
  id: string;
  type: string;
  status: JobStatus;
  progress: number;
  message: string;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  duration?: string;
  timeoutSeconds: number;
  parentRequestId?: string;
}

export interface JobDetailDisplayInfo extends JobDisplayInfo {
  input: JobInput;
  output?: JobOutput;
  errorDetails?: unknown;
  controlFlags: {
    cancelRequested: boolean;
    pauseRequested: boolean;
    timeoutAt: string;
  };
  hasErrors: boolean;
  errorCount: number;
  successRate?: number;
  downloadResults?: DownloadResult[];
}

// Status badge color mapping
export const JOB_STATUS_COLORS: Record<JobStatus, string> = {
  queued: 'bg-gray-100 text-gray-800',
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  cancelled: 'bg-yellow-100 text-yellow-800',
  paused: 'bg-orange-100 text-orange-800',
  timeout: 'bg-red-100 text-red-800',
  pending: 'bg-gray-100 text-gray-800',
  failed: 'bg-red-100 text-red-800',
  partial: 'bg-yellow-100 text-yellow-800'
};

// Job type display mapping
export const JOB_TYPE_LABELS: Record<string, string> = {
  data_intake: 'Data Intake',
  data_processing: 'Data Processing',
  data_validation: 'Data Validation',
  data_export: 'Data Export',
  maintenance: 'Maintenance',
  cleanup: 'Cleanup'
}; 

// CHIRPS Data Intake Job Output Types
export interface ChirpsOutputSummary {
  total_files_processed: number;
  newly_downloaded: number;
  already_downloaded_duplicates: number;
  failed: number;
  success_rate_percent: number;
  error_threshold_exceeded: boolean;
  max_errors_allowed: number;
}

export interface ChirpsOutputDownloadMetrics {
  total_mb_downloaded: number;
  avg_file_size_mb: number;
  download_url: string;
  storage_path: string;
}

export interface ChirpsOutputDataSpecification {
  data_source: string;
  temporal_resolution: string;
  region: string;
  format: string;
  quality: string | null;
  years_requested: number[];
}

export interface ChirpsOutputTiming {
  job_started_at: string;
  processing_duration_seconds: number | null;
}

export interface ChirpsDownloadedFile {
  filename: string;
  size_mb: number;
  checksum: string;
}

export interface ChirpsFailedFile {
  filename: string;
  error: string;
  url: string;
}

export interface ChirpsOutputFileDetails {
  newly_downloaded_files: ChirpsDownloadedFile[];
  failed_files: ChirpsFailedFile[];
}

export interface ChirpsOutputDatabaseRecords {
  raw_data_entries_created: number;
  duplicate_files_skipped: number;
}

export interface ChirpsJobOutput {
  summary: ChirpsOutputSummary;
  download_metrics: ChirpsOutputDownloadMetrics;
  data_specification: ChirpsOutputDataSpecification;
  timing: ChirpsOutputTiming;
  file_details: ChirpsOutputFileDetails;
  database_records: ChirpsOutputDatabaseRecords;
} 